import * as React from 'react';
import { resolve } from 'inversify-react';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';

import { ControllerProps } from 'src/router';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { InvoiceListDataService } from 'src/app/accounting/shared/invoices/list/invoice-list.data';
import { SupplierInvoiceFormView } from './supplier-invoice-form.view';
import { DataLocalService } from 'src/core/services/data-local.service';
import { BillingService } from 'src/app/accounting/shared/billing.service';

interface State {
	filters?: SupplierInvoiceFilters;
	invoices: DataSet<InvoiceSummary>;
	paymentDate?: Date;
	method?: number;
	paymentReference?: string;
	currencyId?: number;
	currencyRate?: number;
	payloads?: { [invoiceId: number]: SelectableInvoicePaymentPayload };
}

@observer
export class SupplierInvoiceFormController extends React.Component<ControllerProps<{}>, State> {
	@resolve
	private toast: ToastNotifications;

	@resolve
	private billingService: BillingService;

	private dataService: InvoiceListDataService;

	private filtersMap = {
		suppliersId: (value: number[]): string => `(${value.map(m => `it.supplierId == ${m}`).join(' || ')})`,
		invoiceDateFrom: (value: Date): string => `it.billingDate >= ${value.getTime()}`,
		invoiceDateTo: (value: Date): string => `it.billingDate <= ${value.getTime()}`,
		dueDate: (value: Date): string => `it.dueDate == '${value.getTime()}`,
		invoiceReferenceNumber: (value: string): string => value === '' ? 'true' : `it.invoiceReferenceNumber.contains('${value}')`,
		showPaid: (value: boolean): string => `it.status ${value ? '==' : '!='} 2`
	};

	constructor(props: Readonly<ControllerProps<any>>) {
		super(props);

		this.state = {
			filters: {
				showPaid: false
			},
			invoices: new DataLocalService<InvoiceSummary>([]),
			paymentDate: new Date(),
			method: 0,
			paymentReference: '',
			currencyId: 1, // GBP
			currencyRate: 1,
			payloads: {}
		};
	}

	componentDidMount(): void {
		this.dataService = InvoiceListDataService.make();

		this.dataService.addFilter('isDeleted', 'eq', false);
		this.dataService.addFilter('accountingTarget', 'eq', 4); // supplier
		this.dataService.addSorting('id', 'asc');
		this.dataService.pageSize = 1000;
	}

	private async performAsync<T>(body: () => Promise<T>): Promise<T> {
		const { setBusy } = this.props;

		setBusy(true);
		try {
			const result = await body();
			return result;
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	}

	@autobind
	private async handleDeleteInvoice(invoice: InvoiceSummary): Promise<void> {
		this.performAsync(async () => {
			await this.dataService.delete([invoice]);
			await this.handleApplyFilters();
		});
	}

	@autobind
	private handleChangePaymentDate(paymentDate: Date): void {
		this.setState({ paymentDate });
	}

	@autobind
	private handleChangePaymentMethod(method: number): void {
		this.setState({ method });
	}

	@autobind
	private handleChangePaymentReference(paymentReference: string): void {
		this.setState({ paymentReference });
	}

	@autobind
	private handleChangePaymentCurrencyId(currencyId: number): void {
		this.setState({ currencyId });
	}

	@autobind
	private handleChangeFilter(fieldName: keyof SupplierInvoiceFilters, value: any = null): void {
		const { filters } = this.state;
		const newFilters = { ...filters, [fieldName]: value };

		this.setState({ filters: newFilters });
	}

	@autobind
	private async handleApplyFilters(): Promise<void> {
		this.performAsync(async () => {
			const { filters, payloads } = this.state;

			const predicate = Object.getOwnPropertyNames(filters)
				.filter((fieldName) => filters[fieldName] != null && fieldName in this.filtersMap)
				.map((fieldName) => this.filtersMap[fieldName](filters[fieldName]))
				.join(' && ');

			this.dataService.params = {};
			Object.getOwnPropertyNames(filters)
				.filter((fieldName) => filters[fieldName] != null && !(fieldName in this.filtersMap))
				.forEach((fieldName) => {
					this.dataService.params[fieldName] = filters[fieldName];
				});

			const invoices = await this.dataService.load((query) => predicate
				? query.filter(predicate)
				: query);

			const newPayments = {};
			invoices.forEach((m) => {
				newPayments[m.id] = m.id in payloads
					? payloads[m.id]
					: {
						invoiceId: m.id,
						checked: false,
						amount: 0,
						otherProps: {
							description: ''
						}
					};
			});

			this.setState({
				payloads: newPayments,
				invoices: new DataLocalService<InvoiceSummary>(invoices)
			});
		});
	}

	@autobind
	private handleChangeSelection(selectedIds: number[]): void {
		const { payloads } = this.state;

		const newPayloads = {};
		this.dataService.items.forEach(m => {
			const payload = payloads[m.id];
			const isChecked = selectedIds.findIndex(p => p === m.id) != -1;
			newPayloads[m.id] = {
				...payload,
				checked: isChecked,
				amount: isChecked ? m.totalNet - m.paid : 0
			}
		});

		this.setState({ payloads: newPayloads });
	}

	@autobind
	private handleEditPayment(invoiceId: number, fieldName: keyof InvoicePaymentPayload, newValue: any): void {
		const { payloads } = this.state;
		const newPayments = {
			...payloads,
			[invoiceId]: {
				...payloads[invoiceId],
				[fieldName]: newValue
			}
		};

		this.setState({ payloads: newPayments });
	}

	@autobind
	private async handleGeneratePayments(): Promise<void> {
		this.performAsync(async () => {
			const { paymentDate, method, paymentReference, currencyId, currencyRate, payloads } = this.state;
			const suppliersId = this.dataService.items
				.filter(m => payloads[m.id] != null && payloads[m.id].checked)
				.map(m => m.supplierId);

			if (suppliersId.length === 0 || suppliersId.some(m => m !== suppliersId[0])) {
				this.toast.showToast('Invalid Operation', "Can't make an invoice for multiples suppliers", 'error');
				return;
			}

			const invoicePayments: InvoicePaymentPayload[] = Object.getOwnPropertyNames(payloads)
				.filter((invoiceId) => payloads[invoiceId].checked)
				.map((invoiceId) => payloads[invoiceId]);

			const paymentInfo: PaymentInfo = {
				amount: invoicePayments.reduce((prev, curr) => prev + curr.amount, 0),
				paymentDate,
				method,
				paymentReference,
				currencyId,
				currencyRate, // TODO: Check with Accounting team what to do!
				accountingTarget: 4,
				serviceName: '',
				cardToken: '',
				supplierId: suppliersId[0],
				billingAddress: null as any,
				otherProps: {}
			};
			await this.billingService.registerPayment(invoicePayments, paymentInfo, true);
			this.toast.showToast('Invoices paid!', '', 'success');

			await this.handleApplyFilters();
		});
	}

	render(): React.ReactNode {
		const { setBusy } = this.props;
		const { filters, invoices, payloads, paymentDate, method, paymentReference, currencyId } = this.state;

		return (
			<SupplierInvoiceFormView
				setBusy={setBusy}
				model={invoices}
				onDeleteInvoice={this.handleDeleteInvoice}
				filters={filters}
				payments={payloads}
				paymentDate={paymentDate}
				paymentMethod={method}
				paymentReference={paymentReference}
				paymentCurrencyId={currencyId}
				onChangeFilter={this.handleChangeFilter}
				onApplyFilters={this.handleApplyFilters}
				onChangePaymentDate={this.handleChangePaymentDate}
				onChangePaymentMethod={this.handleChangePaymentMethod}
				onChangePaymentReference={this.handleChangePaymentReference}
				onChangePaymentCurrencyId={this.handleChangePaymentCurrencyId}
				onChangeSelection={this.handleChangeSelection}
				onEditPayment={this.handleEditPayment}
				onGeneratePayments={this.handleGeneratePayments}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/accounting/suppliers/invoice/supplier-invoice-form.controller.tsx